/** Available agents for the internal agent selector. */
export interface AgentOption {
  id: string;
  label: string;
  entryPoint: string;
  /**
   * Server agent name to pin this selection to. Sent to the API so routing rules are bypassed and this
   * exact agent is used, regardless of entry point / auth / region / puid. Keep in sync with the
   * AgentRouting rules' AgentName values in the API appsettings.
   *
   * OMIT IT for any entry point whose rule is gated (RequiresAuth / Puids): pinning the agent skips
   * exactly those checks, so a pinned allowlisted agent would be reachable by anyone who can pick it
   * in the selector. Leaving it unset routes by entry point instead, which enforces the gate.
   */
  agentName?: string;
  description: string;
}

export const agentOptions: AgentOption[] = [
  {
    id: 'default',
    label: 'CMSP Support Agent',
    entryPoint: 'portal',
    agentName: 'cmsp-support-agent',
    description: 'Default production support agent',
  },
  {
    id: 'ka',
    label: 'CMSP Peridot Support (KA)',
    entryPoint: 'uat-ka',
    agentName: 'cmsp-support-agent-KA',
    description: 'Knowledge Agent–grounded support agent with the classic Contact support handoff flow (UAT)',
  },
  {
    id: 'ka-escalation',
    label: 'CMSP Peridot Support (KA, in-chat escalation)',
    entryPoint: 'uat-ka-escalation',
    // Route by entry point so the selector follows each environment's configured routes.
    description: 'Unrestricted KA assisted in-chat escalation in NPE and PPE. Emerald retains its allowlisted uat-ka pilot in PPE.',
  },
  {
    id: 'ka-multi-locale',
    label: 'CMSP Peridot Support (KA, localised sources)',
    entryPoint: 'uat-ka-multi-locale',
    agentName: 'cmsp-support-agent-KA-multi-locale',
    description: 'KA agent with translated citation titles — multi-locale experiment, does not affect the KA agent',
  },
  // Routed by ENTRY POINT ONLY (no agentName pin): the uat-ka-signin rule is RequiresAuth-gated, and
  // pinning the agent would bypass exactly that sign-in check.
  {
    id: 'ka-signin',
    label: 'CMSP Peridot Support (KA, sign-in enabled)',
    entryPoint: 'uat-ka-signin',
    description: 'Knowledge Agent–grounded support agent with the prod-signin skill enabled (UAT, signed-in only)',
  },
  {
    id: 'shopper',
    label: 'CMSP Support Agent (Shopper)',
    entryPoint: 'shopper',
    agentName: 'cmsp-support-agent-shopper',
    description: 'Copy of CMSP Support Agent for model testing (does not affect the production agent)',
  },
  {
    id: 'skill-based',
    label: 'CMSP Support Agent (Skill-based)',
    entryPoint: 'skill-based',
    agentName: 'cmsp-support-agent-skill-based',
    description: 'Consumer skill-based support agent: base model + per-product skills that clarify then build a precise KB query',
  },
  {
    id: 'skill-based-articles',
    label: 'CMSP Support Agent (Skill-based Articles)',
    entryPoint: 'skill-based-articles',
    agentName: 'cmsp-support-agent-skill-based-articles',
    description: 'Skill-based support-agent twin grounded directly on the support-articles search index',
  },
  {
    id: 'rewards',
    label: 'Microsoft Rewards',
    entryPoint: 'rewards',
    agentName: 'cmsp-rewards-agent',
    description: 'Microsoft Rewards Q&A (KB-grounded)',
  },
  {
    id: 'signin-auth',
    label: 'Sign-In Helper (Authenticated)',
    entryPoint: 'signin-auth',
    agentName: 'cmsp-signin-auth-agent',
    description: 'Consumer sign-in support with human handoff',
  },
  {
    id: 'signin-unauth',
    label: 'Sign-In Helper (Unauthenticated)',
    entryPoint: 'uat-ka',
    agentName: 'cmsp-signin-unauth-agent',
    description: 'Consumer sign-in self-help. Reached via the uat-ka entry point when signed out; signed-in sessions get the KA support agent (auth-aware routing).',
  },
  // Routed by entry point only, like the disambiguation entries below — the base cortex-direct rule
  // ships DISABLED and is enabled per-environment (appsettings.Development.json for local runs), so
  // pinning the agent here would reach it regardless of that switch.
  {
    id: 'cortex-direct',
    label: 'CMSP → Cortex (Direct)',
    entryPoint: 'cortex-direct',
    description: 'Streams every turn straight to Cortex — no CMSP disambiguation and no specialist catalogue; Cortex picks the agent. On escalation the customer sees Cortex\u2019s own message and goes to a human (no handoff back to CMSP). Enabled locally via the Development route overlay.',
  },
  {
    id: 'cortex-disambig',
    label: 'CMSP Disambiguation → Cortex',
    entryPoint: 'cortex-disambig',
    agentName: 'cmsp-support-agent-disambig',
    description: 'CMSP disambiguates, then routes to a specialist via Cortex (WIP; normal help for now).',
  },
  // Same disambiguation behaviour as cortex-disambig, different specialist catalogue: this agent is
  // mapped to Q&A + Investigate a Charge
  // (SpecialistCatalogue:AgentSpecialistMapping), while cortex-disambig is mapped to Q&A only. Specialists
  // are defined once, so the mapping is the only difference, which is what makes this pair the test
  // case for per-agent specialist scoping.
  //
  // Pinned by agentName like cortex-disambig. Safe because the cortex-disambig-billing rule is ungated —
  // the PUID allowlist lives on uat_ka_pilot_billing (the uat-ka rule Emerald routes through), not here.
  {
    id: 'cortex-disambig-billing',
    label: 'CMSP Disambiguation → Cortex (Outlook + Billing)',
    entryPoint: 'cortex-disambig-billing',
    agentName: 'cmsp-support-agent-disambig-billing',
    description: 'CMSP disambiguates, then routes to Q&A or Investigate a Charge via Cortex.',
  },
  {
    id: 'cortex-disambig-surface',
    label: 'CMSP Disambiguation → Cortex (Outlook + Surface Repair)',
    entryPoint: 'cortex-disambig-surface',
    agentName: 'cmsp-support-agent-disambig-surface',
    description: 'CMSP handles general and Surface troubleshooting, routes Outlook to Q&A, and routes eligible accepted repair requests to Surface Repair Order Agent via Cortex.',
  },
  // Clone of cortex-disambig-surface (ADR-0023): the Cortex-handoff behaviour lives on its own agent
  // behind its own entry point so the existing cortex-disambig-surface testers are unaffected. Pinned
  // by agentName like its sibling — this route is ungated by auth/puid, only by the
  // cortex.request_cortex_handoff.enabled flag (author/npe only; off on base/ppe/prod), so pinning the
  // agent doesn't bypass any access check.
  {
    id: 'cortex-disambig-surface-handoff',
    label: 'CMSP Disambiguation → Cortex (Surface, direct handoff)',
    entryPoint: 'cortex-disambig-surface-handoff',
    agentName: 'cmsp-support-agent-disambig-surface-cortex',
    description: 'Collects Product/Device/Problem, then calls request_cortex_handoff to hand off directly to Cortex (no local Knowledge Agent). Gated by cortex.request_cortex_handoff.enabled (author/npe only).',
  },
  // UNPINNED on purpose. Every other Cortex option here pins by agentName, which is safe only because
  // those rules are ungated. This rule carries RequiresAuth: true — not as a rollout gate but because
  // the agent's charge investigation reads Shopper on behalf of the signed-in customer — and pinning
  // skips exactly that check, which would land a signed-out tester on an agent whose every commerce
  // lookup fails. Leaving it unpinned means a signed-out tester simply does not reach it, which is a
  // legible failure instead of a broken-looking one.
  {
    id: 'cortex-disambig-skill',
    label: 'CMSP Disambiguation → Cortex (Outlook + Surface) + local Charge Investigation',
    entryPoint: 'cortex-disambig-skill',
    description:
      'CMSP investigates charges itself using its own Shopper lookups, and routes Outlook to Q&A and Surface repair to Surface Repair Order Agent via Cortex.',
  },
  // Author-only open-weight model regression agents. Copies of CMSP Support Agent for model
  // testing (do not affect the production agent) — see appsettings.author.json AgentRouting:Rules.
  {
    id: 'model-test-llama',
    label: 'CMSP Support Agent (Llama 3.3 70B)',
    entryPoint: 'model-test-llama-3-3-70b',
    agentName: 'cmsp-support-agent-llama-3-3-70b',
    description: 'Copy of CMSP Support Agent on Llama-3.3-70B-Instruct for model testing (Author only)',
  },
  {
    id: 'model-test-deepseek',
    label: 'CMSP Support Agent (DeepSeek V4 Pro)',
    entryPoint: 'model-test-deepseek-v4-pro',
    agentName: 'cmsp-support-agent-deepseek-v4-pro',
    description: 'Copy of CMSP Support Agent on DeepSeek-V4-Pro for model testing (Author only)',
  },
  {
    id: 'model-test-mistral',
    label: 'CMSP Support Agent (Mistral Large 3)',
    entryPoint: 'model-test-mistral-large-3',
    agentName: 'cmsp-support-agent-mistral-large-3',
    description: 'Copy of CMSP Support Agent on Mistral-Large-3 for model testing (Author only)',
  },
];
